import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  FiCamera,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiGift,
  FiMail,
  FiMapPin,
  FiPackage,
  FiPlus,
  FiPrinter,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiTruck,
  FiUser,
  FiX,
} from 'react-icons/fi';

import { Html5Qrcode } from 'html5-qrcode';
import api from '../services/api';
import './Vouchers.css';

import {
  createVoucherPdfBytes,
  getVoucherNumber,
  openVoucherPdf,
} from './generateVoucherPdf';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Alle' },
  { value: 'open', label: 'Offen' },
  { value: 'prepared', label: 'Vorbereitet' },
  { value: 'shipped', label: 'Versendet' },
  { value: 'redeemed', label: 'Eingelöst' },
];

const STATUS_LABELS = {
  open: 'Offen',
  prepared: 'Vorbereitet',
  shipped: 'Versendet',
  redeemed: 'Eingelöst',
  cancelled: 'Storniert',
};

const STATUS_ICONS = {
  open: FiGift,
  prepared: FiPackage,
  shipped: FiTruck,
  redeemed: FiCheck,
};

function formatDate(value) {
  if (!value) return '–';

  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatAmount(amount, currency = 'EUR') {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(Number(amount || 0));
}

function getNextStatus(status) {
  switch (status) {
    case 'open':
      return 'prepared';
    case 'prepared':
      return 'shipped';
    case 'shipped':
      return 'redeemed';
    default:
      return null;
  }
}

function getNextStatusLabel(status) {
  switch (status) {
    case 'open':
      return 'Als vorbereitet markieren';
    case 'prepared':
      return 'Als versendet markieren';
    case 'shipped':
      return 'Als eingelöst markieren';
    default:
      return null;
  }
}



function parseVoucherQrValue(value) {
  const text = String(value || '').trim();

  const match = text.match(
    /^VOUCHER:(PI-\d{8}-(\d+))$/i
  );

  if (!match) {
    return null;
  }

  const voucherNumber = match[1].toUpperCase();
  const voucherId = Number(match[2]);

  if (!Number.isInteger(voucherId) || voucherId <= 0) {
    return null;
  }

  return {
    voucherNumber,
    voucherId,
  };
}

function Vouchers({ selectedLocationId }) {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [expandedVoucherIds, setExpandedVoucherIds] = useState({});
  const [scannerError, setScannerError] = useState('');
  const [scannedVoucher, setScannedVoucher] = useState(null);
  const scannerRef = useRef(null);
  const scannerBusyRef = useRef(false);
  const [vouchers, setVouchers] = useState([]);
  const [summary, setSummary] = useState({});
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [shippingInputs, setShippingInputs] = useState({});
  const [error, setError] = useState('');
  const [printingId, setPrintingId] = useState(null);
  const [createModalOpen, setCreateModalOpen] =
    useState(false);

  const [creatingVoucher, setCreatingVoucher] =
    useState(false);

  const [createdVoucher, setCreatedVoucher] =
    useState(null);

  const [createVoucherError, setCreateVoucherError] =
    useState('');

  const [sharingId, setSharingId] = useState(null);

  const [manualVoucherForm, setManualVoucherForm] =
    useState({
      amount: '',
      buyerName: '',
      recipientName: '',
    });

  const openCreateVoucherModal = () => {
    setCreatedVoucher(null);
    setCreateVoucherError('');

    setManualVoucherForm({
      amount: '',
      buyerName: '',
      recipientName: '',
    });

    setCreateModalOpen(true);
  };

  const closeCreateVoucherModal = () => {
    if (creatingVoucher) return;

    setCreateModalOpen(false);
    setCreatedVoucher(null);
    setCreateVoucherError('');
  };

  const handleManualVoucherChange = event => {
    const { name, value } = event.target;

    setManualVoucherForm(current => ({
      ...current,
      [name]: value,
    }));

    if (createVoucherError) {
      setCreateVoucherError('');
    }
  };

  const handleCreateManualVoucher = async event => {
    event.preventDefault();

    const amount = Number(
      String(manualVoucherForm.amount)
        .replace(',', '.')
        .trim()
    );

    const buyerName =
      manualVoucherForm.buyerName.trim();

    const recipientName =
      manualVoucherForm.recipientName.trim();

    if (!selectedLocationId) {
      setCreateVoucherError(
        'Bitte zuerst einen Standort auswählen.'
      );
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setCreateVoucherError(
        'Bitte einen gültigen Gutscheinwert eingeben.'
      );
      return;
    }

    if (!buyerName) {
      setCreateVoucherError(
        'Bitte den Käufer eingeben.'
      );
      return;
    }

    if (!recipientName) {
      setCreateVoucherError(
        'Bitte den Empfänger eingeben.'
      );
      return;
    }

    try {
      setCreatingVoucher(true);
      setCreateVoucherError('');

      const response = await api.post(
        '/api/vouchers/manual',
        {
          locationId: Number(selectedLocationId),
          amount,
          buyerName,
          recipientName,
        }
      );

      const voucher = response.data?.voucher;

      if (!voucher) {
        throw new Error(
          'Der Server hat keinen Gutschein zurückgegeben.'
        );
      }

      setCreatedVoucher(voucher);

      await fetchVouchers();
    } catch (err) {
      console.error(
        'Manueller Gutschein konnte nicht erstellt werden:',
        err
      );

      setCreateVoucherError(
        err.response?.data?.message ||
        err.message ||
        'Gutschein konnte nicht erstellt werden.'
      );
    } finally {
      setCreatingVoucher(false);
    }
  };

  const handleShareVoucher = async voucher => {
    try {
      setSharingId(voucher.id);
      setCreateVoucherError('');
      setError('');

      const pdfBytes =
        await createVoucherPdfBytes(voucher);

      const voucherNumber =
        getVoucherNumber(voucher);

      const file = new File(
        [pdfBytes],
        `Gutschein-${voucherNumber}.pdf`,
        {
          type: 'application/pdf',
        }
      );

      const canShareFile =
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({
          files: [file],
        });

      if (canShareFile) {
        await navigator.share({
          title: 'Prime Ink Tattoo Gutschein',
          text: `Prime Ink Tattoo Gutschein ${voucherNumber}`,
          files: [file],
        });

        return;
      }

      await openVoucherPdf(voucher);
    } catch (shareError) {
      if (shareError?.name === 'AbortError') {
        return;
      }

      console.error(
        'Gutschein konnte nicht geteilt werden:',
        shareError
      );

      const message =
        'Teilen wird auf diesem Gerät nicht unterstützt. Die PDF wurde stattdessen geöffnet.';

      setCreateVoucherError(message);
      setError(message);
    } finally {
      setSharingId(null);
    }
  };

  const sendVoucherEmail = async voucher => {
    const recipient = String(voucher.recipient_email || '').trim();
    if (!recipient) { setError('Keine Empfängeradresse hinterlegt.'); return; }
    if (!window.confirm(`Gutschein wirklich per E-Mail an ${recipient} senden?`)) return;
    try {
      setUpdatingId(voucher.id);
      setError('');
      const pdfBytes = await createVoucherPdfBytes(voucher);
      const pdf = new Blob([pdfBytes], { type: 'application/pdf' });
      await api.post(`/api/voucher-email/${voucher.id}/send-email`, pdf, {
        headers: { 'Content-Type': 'application/pdf' },
        timeout: 60000,
      });
      await fetchVouchers();
      window.alert('Gutschein-E-Mail wurde an den Mailserver übergeben.');
    } catch (err) {
      console.error('Gutschein-E-Mail-Versand:', err);
      setError(err.response?.data?.message || 'Versand nicht bestätigt. Bitte vor erneutem Versuch das Postfach prüfen.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handlePrintVoucher = async voucher => {
    try {
      setPrintingId(voucher.id);
      setError('');
      await openVoucherPdf(voucher);
    } catch (printError) {
      console.error('Gutschein-PDF konnte nicht erstellt werden:', printError);
      setError(
        printError?.message || 'Der Gutschein konnte nicht erstellt werden.'
      );
    } finally {
      setPrintingId(null);
    }
  };

  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/api/vouchers', {
        params: {
          locationId: selectedLocationId || undefined,
          status: status === 'all' ? undefined : status,
          search: search.trim() || undefined,
        },
      });

      setVouchers(
        Array.isArray(response.data)
          ? response.data
          : response.data.vouchers || []
      );

      setSummary(
        Array.isArray(response.data)
          ? {}
          : response.data.summary || {}
      );
    } catch (err) {
      console.error('Gutscheine konnten nicht geladen werden:', err);
      setError(
        err.response?.data?.message ||
        'Gutscheine konnten nicht geladen werden.'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId, search, status]);

  useEffect(() => {
    const timeout = setTimeout(fetchVouchers, 250);
    return () => clearTimeout(timeout);
  }, [fetchVouchers]);



  const calculatedSummary = useMemo(() => {
    if (Object.keys(summary).length > 0) {
      return summary;
    }

    return vouchers.reduce(
      (result, voucher) => {
        const currentStatus = voucher.fulfillment_status;

        result.total += 1;
        result[currentStatus] = (result[currentStatus] || 0) + 1;

        return result;
      },
      {
        total: 0,
        open: 0,
        prepared: 0,
        shipped: 0,
        redeemed: 0,
      }
    );
  }, [summary, vouchers]);

  const updateStatus = async (voucher, newStatus) => {
    const confirmationMessages = {
      shipped: 'Gutschein wirklich als versendet markieren?',
      redeemed:
        'Gutschein wirklich als eingelöst markieren? Dieser Schritt sollte nur nach tatsächlicher Einlösung erfolgen.',
    };

    if (
      confirmationMessages[newStatus] &&
      !window.confirm(confirmationMessages[newStatus])
    ) {
      return;
    }

    try {
      setUpdatingId(voucher.id);
      setError('');

      const response = await api.put(
        `/api/vouchers/${voucher.id}/status`,
        {
          status: newStatus,
        }
      );

      const updatedVoucher = response.data.voucher;

      setVouchers(current =>
        current.map(item =>
          Number(item.id) === Number(voucher.id)
            ? updatedVoucher
            : item
        )
      );

      await fetchVouchers();
    } catch (err) {
      console.error('Gutscheinstatus konnte nicht geändert werden:', err);

      setError(
        err.response?.data?.message ||
        'Der Gutscheinstatus konnte nicht geändert werden.'
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const shipVoucher = async (voucher, carrier, tracking) => {
    const number = String(tracking || "").trim();
    if (number.length < 4) {
      setError("Bitte eine gueltige Sendungsnummer eingeben.");
      return;
    }
    if (!window.confirm("Gutschein wirklich als versendet markieren?")) return;
    try {
      setUpdatingId(voucher.id);
      setError("");
      await api.put(`/api/voucher-shipping/${voucher.id}/ship`, {
        shipping_carrier: carrier,
        tracking_number: number,
      });
      await fetchVouchers();
    } catch (err) {
      console.error("Gutscheinversand fehlgeschlagen:", err);
      setError(err.response?.data?.message || "Versand konnte nicht gespeichert werden.");
    } finally {
      setUpdatingId(null);
    }
  };

  const checkVoucherTracking = async (voucher) => {
    try {
      setUpdatingId(voucher.id);
      setError('');
      await api.post(`/api/voucher-shipping/${voucher.id}/check-tracking`);
      await fetchVouchers();
    } catch (err) {
      setError(err.response?.data?.message || 'DHL-Tracking konnte nicht geprüft werden.');
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteVoucher = async (voucher) => {
    const confirmed = window.confirm(
      `Gutschein #${voucher.id} wirklich löschen?\n\n` +
      `${voucher.buyer_name || 'Unbekannter Käufer'} · ` +
      `${formatAmount(voucher.amount, voucher.currency)}\n\n` +
      `Dieser Vorgang kann nicht rückgängig gemacht werden.`
    );

    if (!confirmed) return;

    try {
      setUpdatingId(voucher.id);
      setError('');

      await api.delete(
        `/api/vouchers/${voucher.id}`
      );

      setVouchers(current =>
        current.filter(
          item =>
            Number(item.id) !== Number(voucher.id)
        )
      );

      await fetchVouchers();
    } catch (err) {
      console.error(
        'Gutschein konnte nicht gelöscht werden:',
        err
      );

      setError(
        err.response?.data?.message ||
        'Der Gutschein konnte nicht gelöscht werden.'
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const stopScanner = useCallback(async () => {
    scannerBusyRef.current = false;

    const scanner = scannerRef.current;
    scannerRef.current = null;

    if (!scanner) return;

    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
    } catch (error) {
      console.warn('QR-Scanner konnte nicht gestoppt werden:', error);
    }

    try {
      await scanner.clear();
    } catch {
      // Scanner wurde möglicherweise bereits entfernt.
    }
  }, []);

  const closeScanner = useCallback(async () => {
    await stopScanner();
    setScannerOpen(false);
  }, [stopScanner]);

  const handleScannedValue = useCallback(
    async decodedText => {
      if (scannerBusyRef.current) return;

      const parsed = parseVoucherQrValue(decodedText);

      if (!parsed) {
        setScannerError(
          'Dieser QR-Code ist kein gültiger Prime-Ink-Gutschein.'
        );
        return;
      }

      scannerBusyRef.current = true;
      setScannerError('');

      try {
        const response = await api.get(
          `/api/vouchers/${parsed.voucherId}`
        );

        const voucher = response.data;
        const expectedVoucherNumber =
          getVoucherNumber(voucher).toUpperCase();

        if (expectedVoucherNumber !== parsed.voucherNumber) {
          throw new Error(
            'Die Gutscheinnummer stimmt nicht mit dem Datensatz überein.'
          );
        }

        let resultVoucher = voucher;
        if (voucher.fulfillment_status === 'redeemed') {
          window.alert('Dieser Gutschein wurde bereits eingelöst. Eine erneute Einlösung ist nicht möglich.');
        } else if (voucher.fulfillment_status === 'cancelled') {
          window.alert('Dieser Gutschein ist storniert und kann nicht eingelöst werden.');
        } else if (window.confirm(`Gutschein ${expectedVoucherNumber} über ${formatAmount(voucher.amount, voucher.currency)} jetzt einlösen?`)) {
          const redeemResponse = await api.put(`/api/vouchers/${voucher.id}/status`, { status: 'redeemed' });
          resultVoucher = redeemResponse.data.voucher || { ...voucher, fulfillment_status: 'redeemed' };
        }
        setExpandedVoucherIds({ [voucher.id]: true });
        setScannedVoucher(resultVoucher);
        setVouchers([resultVoucher]);
        setSummary({});
        setStatus('all');
        setSearch('');

        await closeScanner();
      } catch (error) {
        console.error(
          'Gescannten Gutschein laden fehlgeschlagen:',
          error
        );

        setScannerError(
          error.response?.data?.message ||
          error.message ||
          'Der Gutschein wurde nicht gefunden.'
        );

        scannerBusyRef.current = false;
      }
    },
    [closeScanner]
  );

  useEffect(() => {
    if (!scannerOpen) return undefined;

    let cancelled = false;

    const startScanner = async () => {
      try {
        setScannerError('');
        scannerBusyRef.current = false;

        const scanner = new Html5Qrcode(
          'voucher-qr-reader'
        );

        scannerRef.current = scanner;

        await scanner.start(
          {
            facingMode: 'environment',
          },
          {
            fps: 10,
            qrbox: {
              width: 230,
              height: 230,
            },
            aspectRatio: 1,
          },
          decodedText => {
            if (!cancelled) {
              handleScannedValue(decodedText);
            }
          },
          () => {
            // Ungültige Zwischenbilder werden ignoriert.
          }
        );
      } catch (error) {
        console.error('QR-Scanner konnte nicht starten:', error);

        setScannerError(
          'Die Kamera konnte nicht gestartet werden. Bitte erlaube den Kamerazugriff.'
        );
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [
    scannerOpen,
    handleScannedValue,
    stopScanner,
  ]);

  const showAllVouchers = async () => {
    setScannedVoucher(null);
    setSearch('');
    setStatus('all');
    await fetchVouchers();
  };

  return (
    <>
      <main className="vouchers-page">
        <div className="voucher-header-actions">
          <button
            type="button"
            className="voucher-scan-button"
            onClick={() => setScannerOpen(true)}
          >
            <FiCamera />
            <span>Scannen</span>
          </button>

          <button
            type="button"
            className="voucher-create-button"
            onClick={openCreateVoucherModal}
          >
            <FiPlus />
            <span>Verkaufen</span>
          </button>

          <button
            type="button"
            className="voucher-refresh-button"
            onClick={fetchVouchers}
            disabled={loading}
            title="Neu laden"
          >
            <FiRefreshCw className={loading ? 'spinning' : ''} />
          </button>
        </div>

        <section className="voucher-toolbar">
          <label className="voucher-search">
            <FiSearch />

            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Name, E-Mail oder PayPal-ID suchen"
            />
          </label>

          <select
            value={status}
            onChange={event => setStatus(event.target.value)}
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </section>

        {scannedVoucher && (
          <div className="voucher-scan-result">
            <div>
              <FiCheck />
              Gutschein gefunden:
              <strong>
                {' '}
                {getVoucherNumber(scannedVoucher)}
              </strong>
            </div>

            <button
              type="button"
              onClick={showAllVouchers}
            >
              Alle anzeigen
            </button>
          </div>
        )}

        {error && <div className="voucher-error">{error}</div>}

        {loading ? (
          <div className="voucher-empty">Gutscheine werden geladen …</div>
        ) : vouchers.length === 0 ? (
          <div className="voucher-empty">
            <FiGift />
            <strong>Keine Gutscheine gefunden</strong>
            <span>Ändere Suche oder Filter.</span>
          </div>
        ) : (
          <section className="voucher-list">
            {vouchers.map(voucher => {
              const currentStatus = voucher.fulfillment_status;
              const StatusIcon = STATUS_ICONS[currentStatus] || FiGift;
              const isPostOrder =
                (voucher.delivery_method === 'post' &&
                  voucher.order_payment_status === 'paid') ||
                (!voucher.delivery_method &&
                  voucher.payment_status === 'paid' &&
                  Boolean(String(voucher.shipping_address || '').trim()));
              const shippingInput = shippingInputs[voucher.id] || {};

              const nextStatus = getNextStatus(currentStatus);
              const isUpdating =
                Number(updatingId) === Number(voucher.id);

              return (
                <article
                  key={voucher.id}
                  className={`voucher-card status-${currentStatus}`}
                >
                  <button
                    type="button"
                    className="voucher-compact-toggle"
                    aria-expanded={Boolean(expandedVoucherIds[voucher.id])}
                    onClick={() => setExpandedVoucherIds(prev => ({ ...prev, [voucher.id]: !prev[voucher.id] }))}
                  >
                    <span className="voucher-compact-identity">
                      <strong>#{voucher.id} · {formatAmount(voucher.amount, voucher.currency)}</strong>
                      <span>{voucher.buyer_name || 'Käufer unbekannt'} → {voucher.recipient_name || voucher.buyer_name || 'Empfänger nicht angegeben'}</span>
                      <span className="voucher-delivery-summary">
                        {voucher.delivery_method === 'email'
                          ? 'E-Mail-Zustellung'
                          : voucher.delivery_method === 'post'
                            ? 'Postversand'
                            : voucher.delivery_method
                              ? 'Zustellung: ' + voucher.delivery_method
                              : 'Zustellart nicht hinterlegt'}
                        {voucher.delivery_method === 'email' &&
                          ` · ${voucher.delivery_status === 'pending' ? 'Ausstehend' : voucher.delivery_status === 'sent' ? 'Versendet' : voucher.delivery_status === 'delivered' ? 'Zugestellt' : voucher.delivery_status || 'Status unbekannt'}`}
                      </span>
                    </span>
                    <span className={`voucher-status status-${currentStatus}`}>
                      {STATUS_LABELS[currentStatus] || currentStatus}
                    </span>
                    {expandedVoucherIds[voucher.id] ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                  {expandedVoucherIds[voucher.id] && (<>
                  <div className="voucher-card-main">
                    <div className="voucher-card-value">
                      <span>Gutschein #{voucher.id}</span>
                      <strong>
                        {formatAmount(voucher.amount, voucher.currency)}
                      </strong>
                    </div>

                    <div className="voucher-card-content">
                      <div className="voucher-card-title-row">
                        <div>
                          <h2>{voucher.product_name || 'Gutschein'}</h2>

                          <div className="voucher-status-row">
                            

                            {!voucher.seen_at && (
                              <span className="voucher-new-badge">
                                NEU
                              </span>
                            )}
                          </div>

                        </div>

                        <small>
                          Gekauft: {formatDate(voucher.paid_at)}
                        </small>
                      </div>

                      <div className="voucher-details-grid">
                        <div>
                          <FiUser />
                          <span>
                            <small>Käufer</small>
                            <strong>
                              {voucher.buyer_name || 'Nicht angegeben'}
                            </strong>
                          </span>
                        </div>

                        {voucher.recipient_name &&
                          voucher.recipient_name !== voucher.buyer_name && (
                            <div>
                              <FiGift
                                style={{ color: '#d4af37' }}
                              />
                              <span>
                                <small>Empfänger</small>
                                <strong>{voucher.recipient_name}</strong>
                              </span>
                            </div>
                          )}

                        <div>
                          <FiMail />
                          <span>
                            <small>E-Mail</small>
                            <strong>
                              {voucher.buyer_email || 'Nicht angegeben'}
                            </strong>
                          </span>
                        </div>

                        {voucher.delivery_method && (
                          <div>
                            <FiMail />
                            <span>
                              <small>Zustellart</small>
                              <strong>
                                {voucher.delivery_method === 'email'
                                  ? 'Per E-Mail'
                                  : voucher.delivery_method === 'post'
                                    ? 'Per Post'
                                    : voucher.delivery_method}
                              </strong>
                              {voucher.delivery_method === 'email' && (
                                <small>
                                  An: {voucher.recipient_email || 'Nicht angegeben'}
                                  {' · '}
                                  {voucher.delivery_status === 'pending'
                                    ? 'Ausstehend'
                                    : voucher.delivery_status === 'sent'
                                      ? 'Versendet'
                                      : voucher.delivery_status === 'delivered'
                                        ? 'Zugestellt'
                                        : voucher.delivery_status || 'Status unbekannt'}
                                </small>
                              )}
                            </span>
                          </div>
                        )}

                        {voucher.delivery_method === 'post' &&
                          voucher.order_shipping_address &&
                          voucher.order_shipping_address !== voucher.shipping_address && (
                            <div>
                              <FiMapPin />
                              <span>
                                <small>Lieferadresse</small>
                                <strong className="voucher-address">
                                  {voucher.order_shipping_address}
                                </strong>
                              </span>
                            </div>
                          )}

                        {voucher.shipping_address && (
                          <div>
                            <FiMapPin />
                            <span>
                              <small>Versand</small>
                              <strong className="voucher-address">
                                {voucher.shipping_address}
                              </strong>
                            </span>
                          </div>
                        )}
                      </div>


                      {isPostOrder && (
                        <div className="voucher-shipping-panel">
                          <div className="voucher-shipping-header">
                            <strong>
                              Postversand · {voucher.order_number || `Gutschein #${voucher.id}`}
                            </strong>
                            <span className="voucher-shipping-badge">
                              {currentStatus === 'shipped' ? 'Versendet' : 'Zu versenden'}
                            </span>
                          </div>

                          {currentStatus === 'shipped' ? (
                            <div className="voucher-tracking-details">
                              <div className="voucher-tracking-carrier">
                                <FiTruck />
                                <span>{voucher.shipping_carrier || 'Versanddienstleister unbekannt'}</span>
                              </div>

                              {voucher.tracking_number && (
                                <div className="voucher-tracking-number">
                                  {voucher.tracking_number}
                                </div>
                              )}

                              <span className={`voucher-tracking-status status-${voucher.tracking_status || 'not_checked'}`}>
                                {({
                                  not_checked: 'Tracking ausstehend',
                                  pending: 'Tracking ausstehend',
                                  in_transit: 'Unterwegs',
                                  out_for_delivery: 'In Zustellung',
                                  delivered: 'Zugestellt',
                                  exception: 'Zustellproblem',
                                  error: 'Trackingfehler'
                                })[voucher.tracking_status] || 'Tracking ausstehend'}
                              </span>

                              {voucher.tracking_number &&
                                voucher.tracking_status !== 'delivered' &&
                                /^(deutsche post|dhl)$/i.test(voucher.shipping_carrier || '') && (
                                  <button type="button" className="voucher-secondary-button"
                                    disabled={isUpdating} onClick={() => checkVoucherTracking(voucher)}>
                                    <FiRefreshCw /> {isUpdating ? 'Prüfe …' : 'Jetzt prüfen'}
                                  </button>
                                )}

                              {voucher.tracking_description && (
                                <p>{voucher.tracking_description}</p>
                              )}

                              {voucher.tracking_checked_at && (
                                <small>
                                  Letzte Prüfung: {formatDate(voucher.tracking_checked_at)}
                                </small>
                              )}

                              {voucher.tracking_status === 'delivered' &&
                                voucher.tracking_delivered_at && (
                                  <small>
                                    Zugestellt: {formatDate(voucher.tracking_delivered_at)}
                                  </small>
                                )}
                            </div>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="voucher-secondary-button"
                                onClick={() => setShippingInputs(prev => ({
                                  ...prev,
                                  [voucher.id]: {
                                    ...prev[voucher.id],
                                    expanded: !prev[voucher.id]?.expanded
                                  }
                                }))}
                              >
                                {shippingInput.expanded ? 'Schließen' : 'Versand erfassen'}
                              </button>

                              {shippingInput.expanded && (
                                <div className="voucher-shipping-fields">
                                  <select
                                    value={shippingInput.carrier || 'Deutsche Post'}
                                    onChange={e => setShippingInputs(prev => ({
                                      ...prev,
                                      [voucher.id]: {
                                        ...prev[voucher.id],
                                        carrier: e.target.value
                                      }
                                    }))}
                                  >
                                    <option value="Deutsche Post">Deutsche Post</option>
                                    <option value="DHL">DHL</option>
                                    <option value="Hermes">Hermes</option>
                                    <option value="DPD">DPD</option>
                                  </select>
                                  <input
                                    type="text"
                                    placeholder="Sendungsnummer eingeben"
                                    value={shippingInput.tracking || ''}
                                    onChange={e => setShippingInputs(prev => ({
                                      ...prev,
                                      [voucher.id]: {
                                        ...prev[voucher.id],
                                        tracking: e.target.value
                                      }
                                    }))}
                                  />
                                  <button
                                    type="button"
                                    className="voucher-primary-button"
                                    disabled={isUpdating}
                                    onClick={() => shipVoucher(
                                      voucher,
                                      shippingInput.carrier || 'Deutsche Post',
                                      shippingInput.tracking
                                    )}
                                  >
                                    Als versendet markieren
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      <div className="voucher-dates">
                        {voucher.shipped_at && (
                          <span>
                            Versendet: {formatDate(voucher.shipped_at)}
                          </span>
                        )}

                        {voucher.redeemed_at && (
                          <span>
                            Eingelöst: {formatDate(voucher.redeemed_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="voucher-card-actions">
                    {voucher.delivery_method === 'email' && voucher.delivery_status === 'pending' &&
                      voucher.payment_status === 'paid' && voucher.order_payment_status === 'paid' &&
                      ['open', 'prepared'].includes(voucher.fulfillment_status) && (
                      <button
                        type="button"
                        className="voucher-secondary-button"
                        onClick={() => sendVoucherEmail(voucher)}
                        disabled={isUpdating}
                        title="Gutschein als PDF per E-Mail versenden"
                      >
                        <FiMail />
                        {updatingId === voucher.id ? 'Wird gesendet …' : 'Per E-Mail senden'}
                      </button>
                    )}

                    <button
                      type="button"
                      className="voucher-secondary-button voucher-print-button"
                      onClick={() => handlePrintVoucher(voucher)}
                      disabled={isUpdating || printingId === voucher.id}
                      title="Gutschein als scharfe PDF öffnen"
                    >
                      <FiPrinter />
                      {printingId === voucher.id ? 'PDF wird erstellt …' : 'Drucken'}
                    </button>

                    <button
                      type="button"
                      className="voucher-secondary-button voucher-delete-button"
                      onClick={() => deleteVoucher(voucher)}
                      disabled={isUpdating}
                      title="Gutschein löschen"
                    >
                      Löschen
                    </button>

                    {currentStatus !== 'open' &&
                      currentStatus !== 'redeemed' && (
                        <button
                          type="button"
                          className="voucher-secondary-button"
                          disabled={isUpdating}
                          onClick={() =>
                            updateStatus(
                              voucher,
                              currentStatus === 'shipped'
                                ? 'prepared'
                                : 'open'
                            )
                          }
                        >
                          Zurück
                        </button>
                      )}

                    {nextStatus && !(nextStatus === 'shipped' && (isPostOrder || voucher.delivery_method === 'email')) && (
                      <button
                        type="button"
                        className={`voucher-primary-button ${nextStatus === 'redeemed'
                          ? 'voucher-manual-redeem'
                          : ''
                          }`}
                        disabled={isUpdating}
                        onClick={() =>
                          updateStatus(voucher, nextStatus)
                        }
                      >
                        {isUpdating
                          ? 'Wird gespeichert …'
                          : getNextStatusLabel(currentStatus)}
                      </button>
                    )}

                    {currentStatus === 'redeemed' && (
                      <span className="voucher-complete-label">
                        <FiCheck />
                        Abgeschlossen
                      </span>
                    )}
                  </div>
                  </>)}
                </article>
              );
            })}
          </section>
        )}
      </main>

      {createModalOpen && (
        <div
          className="voucher-create-backdrop"
          onClick={closeCreateVoucherModal}
        >
          <div
            className="voucher-create-modal"
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              className="voucher-create-close"
              onClick={closeCreateVoucherModal}
              disabled={creatingVoucher}
              aria-label="Fenster schließen"
            >
              <FiX />
            </button>

            {!createdVoucher ? (
              <>
                <div className="voucher-create-heading">
                  <FiGift />

                  <div>
                    <h2>Gutschein verkaufen</h2>
                    <p>
                      Gutschein direkt im Studio erstellen.
                    </p>
                  </div>
                </div>

                <form
                  className="voucher-create-form"
                  onSubmit={handleCreateManualVoucher}
                >
                  <label>
                    <span>Gutscheinwert</span>

                    <div className="voucher-amount-input">
                      <input
                        type="text"
                        inputMode="decimal"
                        name="amount"
                        value={manualVoucherForm.amount}
                        onChange={handleManualVoucherChange}
                        placeholder="100"
                        autoFocus
                      />

                      <strong>€</strong>
                    </div>
                  </label>

                  <label>
                    <span>Käufer</span>

                    <div className="voucher-create-input">
                      <FiUser />

                      <input
                        type="text"
                        name="buyerName"
                        value={manualVoucherForm.buyerName}
                        onChange={handleManualVoucherChange}
                        placeholder="Name des Käufers"
                        autoComplete="off"
                      />
                    </div>
                  </label>

                  <label>
                    <span>Empfänger</span>

                    <div className="voucher-create-input">
                      <FiGift />

                      <input
                        type="text"
                        name="recipientName"
                        value={manualVoucherForm.recipientName}
                        onChange={handleManualVoucherChange}
                        placeholder="Name des Empfängers"
                        autoComplete="off"
                      />
                    </div>
                  </label>

                  {createVoucherError && (
                    <div className="voucher-create-error">
                      {createVoucherError}
                    </div>
                  )}

                  <div className="voucher-create-actions">
                    <button
                      type="button"
                      className="voucher-create-cancel"
                      onClick={closeCreateVoucherModal}
                      disabled={creatingVoucher}
                    >
                      <FiX />
                      Abbrechen
                    </button>

                    <button
                      type="submit"
                      className="voucher-create-submit"
                      disabled={creatingVoucher}
                    >
                      <FiCheck />

                      {creatingVoucher
                        ? 'Wird erstellt …'
                        : 'Gutschein erstellen'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="voucher-created-result">
                <div className="voucher-created-icon">
                  <FiCheck />
                </div>

                <h2>Gutschein erstellt</h2>

                <strong className="voucher-created-number">
                  {getVoucherNumber(createdVoucher)}
                </strong>

                <div className="voucher-created-information">
                  <span>
                    {createdVoucher.recipient_name}
                  </span>

                  <strong>
                    {formatAmount(
                      createdVoucher.amount,
                      createdVoucher.currency
                    )}
                  </strong>
                </div>

                {createVoucherError && (
                  <div className="voucher-create-error">
                    {createVoucherError}
                  </div>
                )}

                <div className="voucher-created-actions">
                  <button
                    type="button"
                    className="voucher-secondary-button"
                    onClick={() =>
                      handlePrintVoucher(createdVoucher)
                    }
                    disabled={
                      printingId === createdVoucher.id ||
                      sharingId === createdVoucher.id
                    }
                  >
                    <FiPrinter />

                    {printingId === createdVoucher.id
                      ? 'PDF wird erstellt …'
                      : 'Drucken'}
                  </button>

                  <button
                    type="button"
                    className="voucher-share-button"
                    onClick={() =>
                      handleShareVoucher(createdVoucher)
                    }
                    disabled={
                      sharingId === createdVoucher.id ||
                      printingId === createdVoucher.id
                    }
                  >
                    <FiSend />

                    {sharingId === createdVoucher.id
                      ? 'Wird vorbereitet …'
                      : 'Teilen'}
                  </button>
                </div>

                <button
                  type="button"
                  className="voucher-created-close"
                  onClick={closeCreateVoucherModal}
                >
                  Schließen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {scannerOpen && (
        <div
          className="voucher-scanner-backdrop"
          onClick={closeScanner}
        >
          <div
            className="voucher-scanner-modal"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="voucher-scanner-close"
              onClick={closeScanner}
            >
              <FiX />
            </button>

            <h2>Gutschein scannen</h2>

            <div id="voucher-qr-reader" />

            <p className="voucher-scanner-hint">
              QR-Code in die Kamera halten
            </p>

            {scannerError && (
              <div className="voucher-scanner-error">
                {scannerError}
              </div>
            )}
          </div>
        </div>
      )}

    </>


  );
}

export default Vouchers;